import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, TrendingUp, Flame, GraduationCap, Bell, ArrowRight } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, SOURCE_LABEL } from "../lib/api";
import { useLookups } from "../lib/lookups";
import { useAuth } from "../contexts/AuthContext";
import DateRangePicker, { computeRange } from "../components/DateRangePicker";

const PIE_COLORS = ["#6C47FF", "#E91E8C", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"];
const G = "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function StatCard({ icon: Icon, label, value, testid, gradient }) {
  const bg = gradient || G;
  return (
    <div
      data-testid={testid}
      className="card card-hover p-5 flex items-center gap-4"
    >
      <div
        className="w-11 h-11 rounded-[11px] flex items-center justify-center flex-shrink-0 text-white"
        style={{ background: bg }}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-[700] text-[#8B90A0] uppercase tracking-[0.12em]">{label}</div>
        <div className="text-[32px] font-[800] text-[#0D0B1F] leading-none mt-0.5" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="card p-5">
      <div className="text-[13px] font-[700] text-[#0D0B1F] mb-5">{title}</div>
      <div style={{ width: "100%", height: 230 }}>{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboard_range");
      if (saved) return JSON.parse(saved);
    } catch {}
    return computeRange("month");
  });
  const { CATEGORIES, STAGES, CATEGORY_COLORS, STAGE_COLORS } = useLookups();
  const { user } = useAuth();

  const handleRangeChange = useCallback((r) => {
    setRange(r);
    try { localStorage.setItem("dashboard_range", JSON.stringify(r)); } catch {}
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    const { data } = await api.get("/dashboard/stats", { params });
    setStats(data);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-[13px] text-[#8B90A0]">Loading dashboard…</div>
      </div>
    );
  }

  const stageData = STAGES.map((s) => ({ name: s, value: stats.by_stage.find((x) => x.name === s)?.value || 0 }));
  const catData = CATEGORIES.map((c) => ({ name: c, value: stats.by_category.find((x) => x.name === c)?.value || 0 }));
  const sourceData = stats.by_source.map((s) => ({ name: SOURCE_LABEL[s.name] || s.name, value: s.value }));
  const courseData = stats.by_course.filter((c) => c.name && c.name !== "—");
  const careerData = stats.by_career.filter((c) => c.name && c.name !== "—");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[13px] text-[#8B90A0] font-[600]">{greeting()},</div>
          <h1 className="text-[28px] font-[800] text-[#0D0B1F] leading-tight mt-0.5">{firstName} 👋</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker value={range} onChange={handleRangeChange} defaultPreset="month" testid="dashboard-range" />
          <Link
            to="/followups"
            data-testid="dashboard-followups-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[9px] border border-[rgba(0,0,0,0.09)] text-[13px] font-[600] min-h-[40px] bg-white hover:bg-[#F5F5FA] text-[#0D0B1F] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
          >
            <Bell className="h-[14px] w-[14px]" /> {stats.due_today + stats.overdue} due
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-total" icon={Users} label="Total Leads" value={stats.total}
          gradient="linear-gradient(135deg, #6C47FF 0%, #8B6FFF 100%)" />
        <StatCard testid="stat-new-week" icon={TrendingUp} label="New This Week" value={stats.new_week}
          gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)" />
        <StatCard testid="stat-hot" icon={Flame} label="Hot Leads" value={stats.hot}
          gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" />
        <StatCard testid="stat-enrolled" icon={GraduationCap} label="Enrolled (Month)" value={stats.enrolled_month}
          gradient="linear-gradient(135deg, #E91E8C 0%, #C2185B 100%)" />
      </div>

      {/* Follow-ups widget */}
      <Link
        to="/followups"
        data-testid="followups-widget"
        className="card card-hover block p-5"
        style={{ borderLeft: "4px solid #6C47FF" }}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-[11px] text-white flex items-center justify-center flex-shrink-0" style={{ background: G }}>
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] font-[700] text-[#8B90A0] uppercase tracking-[0.12em]">Follow-ups</div>
              <div className="text-[14px] font-[700] text-[#0D0B1F] mt-0.5">Today's pipeline check-ins</div>
            </div>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="text-center" data-testid="widget-overdue">
              <div className="text-[28px] font-[800] text-red-600 leading-none">{stats.overdue}</div>
              <div className="text-[10px] text-[#8B90A0] font-[700] uppercase tracking-[0.1em] mt-1">Overdue</div>
            </div>
            <div className="text-center" data-testid="widget-due-today">
              <div className="text-[28px] font-[800] text-amber-600 leading-none">{stats.due_today}</div>
              <div className="text-[10px] text-[#8B90A0] font-[700] uppercase tracking-[0.1em] mt-1">Due Today</div>
            </div>
            <span
              className="btn-mx inline-flex items-center gap-2 px-4 py-2.5 rounded-[9px] text-[13px] font-[700]"
            >
              Open Follow-ups <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Leads by Sub-Stage" testid="chart-by-stage">
          <ResponsiveContainer>
            <BarChart data={stageData} margin={{ top: 5, right: 5, bottom: 30, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F5" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8B90A0" }} angle={-15} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#8B90A0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Bar dataKey="value" isAnimationActive={false} radius={[4, 4, 0, 0]}>
                {stageData.map((d) => <Cell key={d.name} fill={STAGE_COLORS[d.name]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads by Lead Stage" testid="chart-by-category">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name" outerRadius={85} innerRadius={42} isAnimationActive={false}>
                {catData.map((d) => <Cell key={d.name} fill={CATEGORY_COLORS[d.name]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads by Source" testid="chart-by-source">
          <ResponsiveContainer>
            <BarChart data={sourceData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F5" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#8B90A0" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8B90A0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Bar dataKey="value" fill="#6C47FF" isAnimationActive={false} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Course Interest" testid="chart-by-course">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={courseData} dataKey="value" nameKey="name" outerRadius={85} innerRadius={42} isAnimationActive={false}>
                {courseData.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Campaigns" testid="chart-top-campaigns">
          <ResponsiveContainer>
            <BarChart data={stats.top_campaigns} layout="vertical" margin={{ top: 5, right: 5, bottom: 5, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F5" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#8B90A0" }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#8B90A0" }} width={140} />
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Bar dataKey="value" fill="#E91E8C" isAnimationActive={false} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Career Goals" testid="chart-by-career">
          <ResponsiveContainer>
            <BarChart data={careerData} margin={{ top: 5, right: 5, bottom: 40, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F5" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#8B90A0" }} angle={-20} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#8B90A0" }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: "10px", border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: "12px" }} />
              <Bar dataKey="value" fill="#6C47FF" isAnimationActive={false} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
